<!-- src/layout/VipRecharge.vue -->
<template>
  <div class="vip-recharge-page">
    <!-- 移动端顶部导航栏 -->
    <div v-if="isMobile" class="mobile-top-header">
      <div class="mobile-header-left">
        <Icon type="ios-menu" class="menu-icon" @click="goBack" />
        <div class="logo-text">抖数君</div>
      </div>
      <div class="mobile-header-right">
        <Select v-model="currentTheme" @on-change="handleThemeChange" size="small" style="width: 100px;">
          <Option v-for="theme in themeOptions" :key="theme.name" :value="theme.name">
            {{ theme.label }}
          </Option>
        </Select>
        <div class="mobile-user-info" @click="toggleUserDropdown">
          <Avatar :src="require('@/assets/images/default-avatar.png')" size="small" />
          <span class="username">{{ username }}</span>
        </div>
      </div>
    </div>

    <!-- 主卡片 -->
    <Card :class="{ disabled: isCardDisabled }" class="main-card" shadow>
      <!-- 页面标题 -->
      <div class="page-header">
        <h2 class="page-title">VIP套餐充值</h2>
        <p class="current-vip-status" v-if="currentVipStatus.is_vip">
          当前VIP状态: <strong>{{ currentVipStatus.is_vip ? 'VIP会员' : '普通用户' }}</strong>
          <span v-if="currentVipStatus.expire_time"> 到期时间: {{ currentVipStatus.expire_time }}</span>
        </p>
      </div>

      <Divider>选择充值套餐</Divider>

      <!-- VIP套餐列表 -->
      <div class="package-list">
        <div
            v-for="pkg in vipPackages"
            :key="pkg.id"
            :class="['package-item', { active: selectedPackage && selectedPackage.id === pkg.id }]"
            @click="selectPackage(pkg)"
        >
          <div class="package-name">{{ pkg.name }}</div>
          <div class="package-price">￥{{ pkg.price }}</div>
          <div class="package-description">{{ pkg.description }}</div>
          <div class="package-daily-limit">每日限额: {{ pkg.daily_limit }}次</div>
        </div>
      </div>

      <!-- 已选套餐信息 -->
      <div v-if="selectedPackage" class="selected-package">
        <Alert show-icon type="info">
          您选择充值 <strong>{{ selectedPackage.name }}</strong> 套餐，价格 <strong>￥{{ selectedPackage.price }}</strong>
        </Alert>
      </div>

      <!-- 支付方式 -->
      <div v-if="selectedPackage" class="payment-method">
        <p>选择支付方式:</p>
        <RadioGroup v-model="paymentMethod">
          <Radio class="payment-option" label="alipay">
            <Icon color="#1677ff" size="24" type="logo-alipay"/>
            支付宝
            <img
                alt="支付宝Logo"
                class="payment-logo"
                src="@/assets/images/alipay_logo.png"
            />
          </Radio>
          <Radio class="payment-option" label="wxpay">
            <Icon color="#07c160" size="24" type="logo-wechat"/>
            微信支付
            <img
                alt="微信Logo"
                class="payment-logo"
                src="@/assets/images/wechat_logo.png"
            />
          </Radio>
        </RadioGroup>
      </div>

      <!-- 操作按钮 -->
      <div class="actions">
        <Button
            :disabled="!selectedPackage || isCardDisabled"
            :loading="payLoading"
            size="large"
            type="primary"
            @click="openPaymentConfirm"
            class="btn-pay"
        >
          <Icon type="ios-card" class="btn-icon"/>
          立即充值
        </Button>

        <Button
            :disabled="isCardDisabled"
            @click="resetSelection"
            class="btn-reset"
        >
          <Icon type="ios-refresh" class="btn-icon"/>
          重新选择
        </Button>

        <Button
            :disabled="isCardDisabled"
            @click="openRechargeList"
            class="btn-records"
        >
          <Icon type="ios-list-box" class="btn-icon"/>
          充值记录
        </Button>
      </div>

      <!-- 添加倒计时显示 -->
      <div v-if="countdownTime > 0 && paymentStatus === 'pending'" class="countdown-timer">
        <p>请不要关闭页面 支付倒计时: <strong>{{ formatCountdownTime }}</strong></p>
      </div>
    </Card>

    <!-- 支付确认Modal中的内容 -->
    <Modal
        v-model="showPayModal"
        :loading="payModalLoading"
        title="支付确认"
        @on-ok="confirmPay"
        @on-cancel="cancelPay"
    >
      <div class="pay-modal-content">
        <p>您即将购买 <strong>{{ selectedPackage?.name || '' }}</strong> 套餐</p>
        <p>价格: <strong>￥{{ selectedPackage?.price || 0 }}</strong></p>
        <p>支付方式: <strong>{{ paymentMethod === 'alipay' ? '支付宝' : '微信支付' }}</strong></p>
        <Alert show-icon style="margin-top: 15px;" type="warning">
          确认支付后将跳转到支付页面，请确保账户余额充足 请在新窗口中完成支付
        </Alert>
      </div>
    </Modal>

    <!-- 支付结果Modal -->
    <Modal
        v-model="showResultModal"
        :footer-hide="true"
        :mask-closable="true"
        title="支付结果"
        width="360"
    >
      <div class="result-modal-content" style="text-align: center;">
        <!-- 成功图标 -->
        <Icon
            v-if="payResult === 'success'"
            size="50" style="color: #19be6b;"
            type="ios-checkmark-circle"
        />
        <!-- 失败图标 -->
        <Icon
            v-else-if="payResult === 'fail'"
            size="50" style="color: #ed4014;"
            type="ios-close-circle"
        />
        <!-- 提示信息 -->
        <p style="font-size: 16px; margin: 15px 0;">{{ resultMessage }}</p>

        <!-- 成功时的操作按钮 -->
        <Button
            v-if="payResult === 'success'"
            type="primary"
            @click="closeResultModal"
        >
          确定
        </Button>
        <!-- 失败时的操作按钮 -->
        <div v-else-if="payResult === 'fail'">
          <Button @click="retryPay">重新支付</Button>
          <Button style="margin-left: 10px;" type="primary" @click="closeResultModal">
            关闭
          </Button>
        </div>
      </div>
    </Modal>
  </div>
</template>

<script>import {Card, Divider, Icon, Modal, Radio} from "view-ui-plus";
import apiClient from '@/api/index';
import QRCode from "qrcode";

export default {
  name: 'VipRecharge',
  components: {Modal, Icon, Radio, Card, Divider},
  data() {
    return {
      // 支付相关状态
      payUrl: '', // 支付链接
      orderId: '', // 订单ID
      paymentStatus: 'pending', // 支付状态：pending / success / fail
      isPaying: false, // 是否正在支付
      showPayModal: false, // 是否显示支付确认模态框
      showResultModal: false, // 是否显示支付结果模态框
      vipPackages: [], // VIP套餐数据
      paymentMethod: 'alipay', // 支付方式，默认支付宝
      currentVipStatus: {
        is_vip: false,
        expire_time: ''
      }, // 当前VIP状态
      selectedPackage: null, // 已选套餐
      payLoading: false, // 支付加载状态
      payModalLoading: false, // 支付确认模态框加载状态
      payResult: '', // 支付结果（'success' 或 'fail'）

      // 支付倒计时
      countdownTime: 0, // 倒计时时间（秒，10分钟）
      countdownInterval: null, // 倒计时定时器

      // 轮询相关
      pollingInterval: null, // 轮询定时器
      pollCount: 0, // 轮询次数
      maxPollAttempts: 120, // 最大轮询次数（每5秒一次，共10分钟）

      // 提示信息
      resultMessage: '', // 支付结果消息

      // 其他数据...
      isUserLoggedIn: false,
      deviceType: 'mobile', // 默认自动判断  移动端还是pc端
      isCardDisabled: false, // 主卡片是否禁用
      
      // 移动端导航栏相关
      username: localStorage.getItem('username') || '用户', // 用户名
      currentTheme: localStorage.getItem('currentTheme') || 'default', // 当前主题
      themeOptions: [
        { name: 'default', label: '经典' },
        { name: 'starry', label: '星空' },
        { name: 'blue', label: '蓝色' },
        { name: 'purple', label: '紫色' },
        { name: 'green', label: '绿色' },
        { name: 'red', label: '红色' },
        { name: 'orange', label: '橙色' }
      ], // 主题选项
    };
  },
  // 计算属性
  computed: {
    formatCountdownTime() {
      const minutes = Math.floor(this.countdownTime / 60);
      const seconds = this.countdownTime % 60;
      return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    },
  },

  methods: {
    // 切换主题
    handleThemeChange(theme) {
      localStorage.setItem('currentTheme', theme);
      document.body.className = 'theme-' + theme;
    },
    // 切换用户下拉菜单
    toggleUserDropdown() {
      this.userDropdownVisible = !this.userDropdownVisible;
    },
    // 返回上一页
    goBack() {
      this.$router.back();
    },
    // 退出登录
    handleLogout() {
      localStorage.removeItem('userLoggedIn');
      localStorage.removeItem('username');
      localStorage.removeItem('userEmail');
      this.$router.push({ name: 'TiktokQuery' });
    },
    checkLoginStatus() {
      const isAuthenticated = localStorage.getItem('userLoggedIn') === 'true'; // 检查用户是否登录
      if (!isAuthenticated && this.$route.name !== 'TiktokQuery') {
        // 如果用户未登录且当前页面不是首页，则跳转到首页
        this.$router.push({ name: 'TiktokQuery' });
      }
    },
    // 打开充值记录页面
    openRechargeList() {
      try {
        const routeUrl = this.$router.resolve({ name: 'RechargeList' });
        window.open(routeUrl.href, '_blank');
      } catch (error) {
        console.error('Failed to open recharge list page:', error);
        this.$Message.error('无法打开充值记录页面，请稍后重试');
      }
    },
    isWeixinBrowser() {
      const ua = navigator.userAgent.toLowerCase();
      return ua.indexOf('micromessenger') !== -1;
    },
    detectDeviceType() {
      const userAgent = navigator.userAgent.toLowerCase();
      if (/iphone|ipad|ipod|android/.test(userAgent)) {
        this.deviceType = 'mobile'; // 手机端
      } else {
        this.deviceType = 'pc'; // PC端
      }
    },
    async fetchVipPackages() { // 获取VIP套餐数据
      try {
        const res = await apiClient.get('vip/packages'); // 调用API获取数据
        if (res.data.code === 200) { // 判断响应状态码
          this.vipPackages = res.data.data; // 更新套餐数据

          // 如果有预设的套餐ID，则自动选择
          const packageId = this.$route.query.package_id;
          if (packageId) {
            const pkg = this.vipPackages.find(p => p.id == packageId);// 查找指定ID的套餐
            if (pkg) {
              this.selectPackage(pkg);// 自动选择套餐
            }
          }
        }
      } catch (err) { // 捕获异常
        this.$Message.error('获取VIP套餐失败: ' + err.message); // 显示错误信息
      }
    },
    async fetchCurrentVipStatus() { // 获取当前VIP状态
      try {
        const res = await apiClient.get('vip/status'); // 调用API获取状态
        if (res.data.code === 200) { // 判断响应状态码
          this.currentVipStatus = res.data.data; // 更新VIP状态
        }
      } catch (err) { // 捕获异常
        console.error('获取VIP状态失败:', err); // 打印错误信息
      }
    },
    selectPackage(pkg) { // 选择套餐
      if (pkg) {  // 添加 null 检查
        this.selectedPackage = pkg;
      }

    },
    /**
     * 打开支付确认 Modal
     */
    openPaymentConfirm() {
      if (!this.selectedPackage) {
        this.$Message.warning('请选择充值套餐');
        return;
      }
      this.showPayModal = true; // 显示支付确认 Modal
    },
    /**
     * 确认支付
     */
    async confirmPay() {
      try {
        this.payModalLoading = true; // 开启加载状态

        // 发送 POST 请求创建订单
        const response = await apiClient.post('vip/create-order', {
          package_id: this.selectedPackage.id,// 套餐ID
          type: this.paymentMethod,// 支付方式
          user_email: localStorage.getItem('userEmail') || '',// 用户邮箱
          device_type: this.deviceType// 设备类型
        });

        if (response.data.code === 1) {
          this.orderId = response.data.data.out_trade_no; // 获取订单ID
          if (this.deviceType === 'mobile') {
            this.qrcodeUrl = response.data.data.qrcode_url; // 提取 qrcode_url
          } else {
            this.payUrl = response.data.data.pay_url; // 获取支付链接
          }

          this.startPayment(); // 跳转到支付界面
          this.startPolling(); // 启动支付状态轮询

        } else {
          this.$Message.error(response.data.msg || '订单创建失败');
        }

      } catch (error) {
        // 捕获异常并显示错误提示
        this.$Message.error('订单创建失败，请稍后重试'+ error);
        //粘贴错误信息
        console.error(error);
        // 临时替换 console.log 为 alert 显示
        alert('调试信息: ' + error);
      } finally {
        this.payModalLoading = false; // 关闭加载状态
        this.showPayModal = false; // 隐藏支付确认 Modal
      }
    },
    /**
     * 开始支付流程
     */
    startPayment() {
      if (!this.payUrl && !this.qrcodeUrl) {
        this.$Message.error('支付链接无效，请重新创建订单');
        return;
      }

      if (this.deviceType === 'mobile') {
        if (this.paymentMethod === 'alipay') {
          // 支付宝支付逻辑
          const qrcodeUrl = this.qrcodeUrl; // 后端返回的 qrcode_url
          if (!qrcodeUrl) {
            this.$Message.error('二维码链接无效，请重新创建订单');
            return;
          }
          // 判断是否是微信浏览器 因为跳转不过去支付宝app，只能弹出支付二维码
          if (this.isWeixinBrowser()) {
            this.showQRCodeModal(qrcodeUrl, 'alipay'); // 传递 paymentMethod 参数
          } else {
            // 构造 alipays:// 跳转链接
            const alipayScheme = `alipays://platformapi/startapp?appId=20000067&url=${encodeURIComponent(qrcodeUrl)}`;
            window.location.href = alipayScheme; // 跳转到支付宝 App
          }
        } else if (this.paymentMethod === 'wxpay') {
          // 微信支付逻辑
          const wechatScheme = this.qrcodeUrl; // 后端返回的 weixin:// 链接
          if (!wechatScheme) {
            this.$Message.error('微信支付链接无效，请重新创建订单');
            return;
          }
          this.showQRCodeModal(wechatScheme, 'wxpay'); // 传递 paymentMethod 参数
        }
      } else {
        // PC 端逻辑：在新窗口打开支付页面
        if (this.paymentMethod === 'alipay') {
          if (!this.payUrl) {
            this.$Message.error('支付宝支付链接无效，请重新创建订单');
            return;
          }
          window.open(this.payUrl, '_blank'); // 打开支付宝支付页面
        } else if (this.paymentMethod === 'wxpay') {
          if (!this.payUrl) {
            this.$Message.error('微信支付链接无效，请重新创建订单');
            return;
          }
          window.open(this.payUrl, '_blank'); // 打开微信支付页面
        }
      }
    },
    // 启动支付倒计时
    startCountdown() {
      if (this.countdownInterval) return; // 如果倒计时已启动，则直接返回

      this.countdownTime = 600; // 初始化倒计时时间（10分钟）
      this.countdownInterval = setInterval(() => {
        if (this.countdownTime > 0) {
          this.countdownTime--;
        } else {
          clearInterval(this.countdownInterval); // 停止倒计时
          this.handlePaymentTimeout(); // 处理超时逻辑
        }
      }, 1000);
    },

    showQRCodeModal(qrCodeImageUrl, paymentMethod) {
      let title = '';
      let logoSrc = '';

      // 根据支付方式设置标题和 Logo
      if (paymentMethod === 'alipay') {
        title = '支付宝支付';
        logoSrc = require('@/assets/images/alipay_logo.png');
      } else if (paymentMethod === 'wxpay') {
        title = '微信支付';
        logoSrc = require('@/assets/images/wechat_logo.png');
      }

      // 确保支付链接有效
      if (!qrCodeImageUrl) {
        this.$Message.error('支付链接无效，无法生成二维码');
        return;
      }

      // 使用 QRCode.toDataURL 生成二维码图片
      QRCode.toDataURL(qrCodeImageUrl)
          .then((qrCodeBase64) => {
            console.log('Generated QR Code Base64:', qrCodeBase64);

            // 显示模态框
            this.$Modal.info({
              title: '',
              content: `            <div style="text-align: center; padding: 20px; border-radius: 8px; box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1); border: 1px solid #ddd;">
              <!-- 顶部绿色标题栏 -->
              <div style="background-color: #e8f5e8; color: #388e3c; padding: 10px; border-radius: 8px; margin-bottom: 8px;">
                <img src="${logoSrc}" alt="${title}Logo" style="width: 24px; margin-right: 8px; vertical-align: middle;" />
                <span style="font-size: 16px; font-weight: bold;">${title}</span>
              </div>

              <!-- 金额显示 -->
              <div style="color: #388e3c; font-size: 20px; margin-bottom: 4px;">
                ¥${this.selectedPackage?.price}</div>

              <!-- 二维码图片 -->
              <img id="qrCodeImage" src="${qrCodeBase64}" alt="${title}二维码" style="width: 250px; height: 250px; margin-bottom: 8px;" />

              <!-- 系统订单号 -->
              <div style="background-color: #2d2d2d; color: white; padding: 10px; border-radius: 8px; margin-top: 8px;">
                <p style="margin: 0; font-size: 14px;">系统订单号：${this.orderId}</p>
                <p style="margin: 0; font-size: 14px;">请使用${paymentMethod === 'alipay' ? '支付宝' : '微信'}扫一扫</p>
                <p style="margin: 0; font-size: 14px;">扫描二维码支付</p>
              </div>
            </div>
          `,
              okText: '关闭',
            });
          })
          .catch((err) => {
            console.error('二维码生成失败:', err);
            this.$Message.error('二维码生成失败，请稍后重试');
          });
    },
    // 保存二维码到相册
    saveQrCode(qrCodeBase64) {
      const link = document.createElement('a'); // 创建一个 <a> 元素
      link.href = qrCodeBase64; // 设置下载链接为二维码图片的 Base64 URL
      link.download = `qrcode_${this.orderId}.png`; // 设置下载文件名
      link.click(); // 模拟点击下载

      // 提示用户保存成功
      this.$Message.success('二维码已保存到相册');
    },

    // 处理支付超时逻辑
    handlePaymentTimeout() {
      this.$Message.error('支付链接已失效，请重新创建订单');
      this.showPayModal = false; // 关闭支付确认模态框
      clearInterval(this.pollingInterval); // 停止轮询
    },
    // 启动支付状态轮询
    startPolling() {
      // 清理旧的轮询定时器
      this.cancelPolling();

      this.pollCount = 0; // 初始化轮询次数

      // 定义轮询函数
      const poll = async () => {
        try {
          // 如果达到最大轮询次数，停止轮询并处理超时逻辑
          if (this.pollCount >= this.maxPollAttempts) {
            this.handlePollTimeout();
            return;
          }

          // 检查订单号是否存在
          if (!this.orderId) {
            this.handleInvalidOrder();
            return;
          }

          // 调用 queryRechargeOrder 方法查询支付状态
          const response = await apiClient.post('recharge/query', {
            out_trade_no: this.orderId, // 传递订单ID
            user_email: localStorage.getItem('userEmail') || '', // 用户邮箱
          });

          // 检查响应数据
          if (response.data.code === 1) {
            const status = response.data.data.status;

            // 根据支付状态处理逻辑
            if (status === 1 || status === '1') {
              this.handlePaymentSuccess(); // 支付成功
              return ; // 提前退出
            } else if (status === 0) {
              console.log('订单未支付');
            } else {
              this.handlePaymentFail(); // 支付失败
              return ; // 提前退出
            }
          } else {
            console.warn('支付状态查询失败:', response.data.msg);
          }
        } catch (error) {
          console.error('支付状态轮询失败:', error);
          this.$Message.error('支付状态检查失败，请稍后重试');
        } finally {
          this.pollCount++; // 增加轮询计数

          // 如果达到最大轮询次数，强制停止轮询
          if (this.pollCount >= this.maxPollAttempts) {
            this.handlePollTimeout();
          }
        }
      };

      // 启动定时器，每5秒执行一次轮询
      this.pollingInterval = setInterval(poll, 5000);
    },
    // 取消支付状态轮询
    cancelPolling() {
      if (this.pollingInterval) {
        console.log('cancelPolling called, clearing interval');
        clearInterval(this.pollingInterval); // 确保定时器被清除
        this.pollingInterval = null; // 重置定时器引用
      }
    },
    // 处理轮询超时逻辑
    handlePollTimeout() {
      clearInterval(this.pollingInterval); // 停止轮询
      this.cancelPolling(); // 清理轮询状态
      this.$Message.error('支付超时，请重新创建订单');
      this.handlePaymentTimeout(); // 处理支付超时逻辑
    },

    // 处理无效订单逻辑
    handleInvalidOrder() {
      clearInterval(this.pollingInterval); // 停止轮询
      this.cancelPolling(); // 清理轮询状态
      this.$Message.error('订单号无效，请重新创建订单');
    },

    // 处理支付成功逻辑
    handlePaymentSuccess() {
      this.cancelPolling(); // 停止轮询
      this.paymentStatus = 'success';

      setTimeout(() => {
        console.log('支付成功：设置 payResult 为 success');
        this.payResult = 'success'; // 确保设置为成功状态
        this.resultMessage = 'VIP套餐购买成功！您的VIP权限已更新。';
        this.$Message.success(this.resultMessage);
        this.showPayModal = false; // 关闭支付确认模态框
        this.showResultModal = true; // 显示支付结果模态框
        this.fetchCurrentVipStatus(); // 更新VIP状态
        this.resetPaymentState(); // 重置支付状态
        this.isCardDisabled = false; // 恢复主卡片状态
      }, 300); // 延迟 300ms，确保状态稳定
    },

    // 处理支付失败逻辑
    handlePaymentFail() {
      this.cancelPolling(); // 停止轮询
      this.paymentStatus = 'fail';

      this.payResult = 'fail';
      this.resultMessage = '支付失败，请重试。';
      this.$Message.error(this.resultMessage);
      this.showResultModal = true; // 显示支付结果模态框
      this.resetPaymentState(); // 重置支付状态
      this.isCardDisabled = false; // 恢复主卡片状态
    },
    // 重置支付状态
    resetPaymentState() {
      this.paymentStatus = 'pending'; // 重置支付状态
      this.payUrl = ''; // 清空支付链接
      this.orderId = ''; // 清空订单ID
      this.countdownTime = 0; // 重置倒计时时间
      clearInterval(this.countdownInterval); // 停止倒计时定时器
      this.countdownInterval = null; // 重置倒计时定时器引用
      this.pollCount = 0; // 重置轮询次数
    },
    cancelPay() { // 取消支付
      this.showPayModal = false; // 关闭支付确认模态框
      this.payModalLoading = false; // 关闭加载状态
      this.resetPaymentState()
    },

    // 处理支付结果模态框关闭逻辑
    closeResultModal() { // 关闭支付结果模态框
      setTimeout(() => {
        this.payResult = ''; // 清空支付结果
        this.resultMessage = '';
        this.showResultModal = false; // 隐藏模态框

        try {
          // 尝试跳转到 TiktokQuery 页面
          this.$router.push({ name: 'TiktokQuery' });
          console.log('跳转成功');
        } catch (error) {
          console.error('跳转失败:', error);
          // 如果跳转失败，可以使用 window.location.href 作为备用方案
          window.location.href = '/tiktok-query';
        }

        // 可以选择刷新页面或更新状态
        // window.location.reload();
      }, 500); // 延迟 500ms 关闭，确保用户看到成功提示
    },
    retryPay() { // 重新支付
      this.showResultModal = false; // 关闭支付结果模态框
      this.showPayModal = true; // 显示支付确认模态框
    },
    resetSelection() { // 重置选择
      this.selectedPackage = null; // 清空已选套餐
      this.payUrl = ''; // 清空支付链接
    }
  },
  mounted() { // 生命周期钩子
    this.checkLoginStatus();// 检查登录状态
    // 设置页面标题
    document.title = 'VIP套餐充值';
    this.detectDeviceType();// 检测设备类型
    this.fetchVipPackages(); // 获取VIP套餐数据
    this.fetchCurrentVipStatus(); // 获取当前VIP状态
    //读取登录状态
    const loggedIn = localStorage.getItem('userLoggedIn') === 'true';
    const token = localStorage.getItem('token');// 获取登录状态token
    if (loggedIn && token){
      this.isUserLoggedIn=true;
    }else{
      this.isUserLoggedIn=false;
    }
    // 应用保存的主题
    const savedTheme = localStorage.getItem('currentTheme') || 'default';
    document.body.className = `theme-${savedTheme}`;
  },
  beforeDestroy() {
    this.cancelPolling(); // 清理轮询定时器
    clearInterval(this.countdownInterval); // 清理倒计时定时器
  },
};
</script>

<style scoped>

/* 移动端顶部导航栏样式 */
.mobile-top-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 15px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 8px;
  margin-bottom: 15px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.mobile-header-left {
  display: flex;
  align-items: center;
  gap: 10px;
}

.mobile-header-left .menu-icon {
  font-size: 24px;
  color: #fff;
  cursor: pointer;
}

.mobile-header-left .logo-text {
  font-size: 18px;
  font-weight: bold;
  color: #fff;
}

.mobile-header-right {
  display: flex;
  align-items: center;
  gap: 10px;
}

.mobile-user-info {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 6px;
  cursor: pointer;
}

.mobile-user-info .username {
  font-size: 14px;
  color: #fff;
  font-weight: 500;
}


/* 已选套餐信息样式 */
.selected-package {
  margin: 20px 0;
  padding: 15px;
  border-radius: 8px;
  background-color: #f0f8ff;
  /*border: 1px solid #d0e9ff;*/
}

/*.selected-package .ivu-alert {
  margin: 0;
  padding: 12px 16px;
  border-radius: 6px;
}*/

/*.selected-package .ivu-alert-info {
  border: 1px solid #b3d9ff;
  background-color: #f0f8ff;
}*/



/* vip-recharge-page 样式 */
.vip-recharge-page {
  padding: 20px; /* 内边距，避免内容紧贴边缘 */
  background-color: #f5f7fa; /* 背景色，提供柔和的视觉效果 */
  min-height: 100vh; /* 最小高度为视口高度，确保页面填满屏幕 */
  display: flex; /* 使用弹性布局 */
  flex-direction: column; /* 子元素垂直排列 */
  align-items: center; /* 水平居中对齐 */
  font-family: 'Arial', sans-serif; /* 字体设置 */
}

/* 页面标题样式 */
.vip-recharge-page .page-title {
  text-align: center; /* 居中对齐 */
  font-size: 18px; /* 字体大小 */
  font-weight: bold; /* 加粗 */
  margin-bottom: 10px; /* 下边距 */
  color: #333; /* 文字颜色 */
}

.current-vip-status {
  text-align: center;
  font-size: 14px;
  color: #515a6e;
  margin-bottom: 20px;
}

/* 主卡片容器样式 */
.vip-recharge-page .main-card {
  width: 100%; /* 占满父容器宽度 */
  max-width: 700px; /* 最大宽度限制 */
  margin: 0 auto; /* 水平居中 */
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1); /* 阴影效果 */
  border-radius: 10px; /* 圆角 */
  background-color: #fff; /* 白色背景 */
  padding: 20px; /* 内边距 */
  box-sizing: border-box; /* 盒模型 */
}

/* 套餐列表 */
.package-list {
  display: flex; /* 弹性布局 */
  flex-wrap: wrap; /* 自动换行 */
  gap: 15px; /* 间距 */
  justify-content: center; /* 居中对齐 */
}

.package-item {
  width: 180px; /* 宽度 */
  border: 2px solid #e8eaec; /* 边框 */
  border-radius: 8px; /* 圆角 */
  text-align: center; /* 居中对齐 */
  padding: 15px; /* 内边距 */
  cursor: pointer; /* 鼠标指针 */
  transition: all 0.3s; /* 过渡动画 */
  background-color: #fff; /* 背景色 */
  box-sizing: border-box; /* 盒模型 */
}

.package-item:hover {
  border-color: #2d8cf0; /* 鼠标悬停时边框颜色 */
  box-shadow: 0 4px 12px rgba(45, 140, 240, 0.2); /* 阴影 */
}

.package-item.active {
  border-color: #2d8cf0; /* 激活状态下边框颜色 */
  background-color: #ecf5ff; /* 背景色 */
  box-shadow: 0 4px 12px rgba(45, 140, 240, 0.2); /* 阴影 */
}

.package-name {
  font-size: 16px; /* 字体大小 */
  font-weight: bold; /* 加粗 */
  color: #333; /* 文字颜色 */
  margin-bottom: 8px; /* 下边距 */
}

.package-price {
  font-size: 20px; /* 字体大小 */
  font-weight: bold; /* 加粗 */
  color: #f60; /* 文字颜色 */
  margin-bottom: 8px; /* 下边距 */
}

.package-description {
  font-size: 12px; /* 字体大小 */
  color: #515a6e; /* 文字颜色 */
  margin-bottom: 8px; /* 下边距 */
  min-height: 40px; /* 最小高度 */
}

.package-daily-limit {
  font-size: 12px; /* 字体大小 */
  color: #2d8cf0; /* 文字颜色 */
  font-weight: bold; /* 加粗 */
}

/* 支付方式 */
.payment-method {
  margin-top: 20px; /* 上边距 */
  text-align: center; /* 居中对齐 */
}

.payment-option {
  display: inline-flex; /* 行内弹性布局 */
  align-items: center; /* 垂直居中 */
  gap: 8px; /* 间距 */
  padding: 10px 15px; /* 内边距 */
  border: 1px solid #e8eaec; /* 边框 */
  border-radius: 8px; /* 圆角 */
  background-color: #fafafa; /* 背景色 */
  transition: all 0.3s; /* 过渡动画 */
  cursor: pointer; /* 鼠标指针 */
  margin-right: 20px; /* 右边距 */
}

.payment-option:hover {
  border-color: #2d8cf0; /* 鼠标悬停时边框颜色 */
  background-color: #ecf5ff; /* 背景色 */
  box-shadow: 0 4px 12px rgba(45, 140, 240, 0.2); /* 阴影 */
}

.payment-option.active {
  border-color: #2d8cf0; /* 激活状态下边框颜色 */
  background-color: #e6f7ff; /* 背景色 */
  box-shadow: 0 4px 12px rgba(45, 140, 240, 0.2); /* 阴影 */
}

.payment-option .ivu-icon {
  font-size: 24px; /* 图标大小 */
}

.payment-option span {
  font-size: 14px; /* 字体大小 */
  color: #515a6e; /* 文字颜色 */
}

.payment-logo {
  width: 40px; /* 宽度 */
  height: auto; /* 高度自动 */
  border-radius: 4px; /* 圆角 */
}

/* 操作按钮 */
.actions {
  margin-top: 30px;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 15px;
  flex-wrap: wrap;
}

.actions Button {
  min-width: 130px;
  height: 44px;
  font-size: 15px;
  border-radius: 8px;
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.btn-icon {
  margin-right: 6px;
  font-size: 16px;
}

.btn-pay {
  background: linear-gradient(135deg, #2d8cf0 0%, #5cadff 100%) !important;
  border: none !important;
  color: #fff !important;
  box-shadow: 0 4px 12px rgba(45, 140, 240, 0.3);
}

.btn-pay:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 18px rgba(45, 140, 240, 0.4);
}

.btn-pay:disabled {
  background: #c5c8ce !important;
  transform: none;
  box-shadow: none;
  color: #fff !important;
}

.btn-reset {
  background: transparent !important;
  border: 1px solid #2d8cf0 !important;
  color: #2d8cf0 !important;
}

.btn-reset:hover {
  background: #ecf5ff !important;
  color: #2d8cf0 !important;
  transform: translateY(-2px);
}

.btn-records {
  background: #f8f9fa !important;
  border: 1px solid #dcdee2 !important;
  color: #606266 !important;
}

.btn-records:hover {
  background: #f0f2f5 !important;
  color: #2d8cf0 !important;
  border-color: #2d8cf0 !important;
  transform: translateY(-2px);
}

/* 星空主题按钮覆盖 */
.theme-starry .btn-pay {
  background: linear-gradient(135deg, #64ffda 0%, #00bcd4 100%) !important;
  color: #0a0e27 !important;
  box-shadow: 0 4px 12px rgba(100, 255, 218, 0.3);
}

.theme-starry .btn-pay:hover {
  box-shadow: 0 6px 18px rgba(100, 255, 218, 0.4);
}

.theme-starry .btn-pay:disabled {
  background: #2a2a4e !important;
  color: #8892b0 !important;
}

.theme-starry .btn-reset {
  border-color: #64ffda !important;
  color: #64ffda !important;
}

.theme-starry .btn-reset:hover {
  background: #1a2745 !important;
  color: #64ffda !important;
}

.theme-starry .btn-records {
  background: #16213e !important;
  border-color: #2a2a4e !important;
  color: #ccd6f6 !important;
}

.theme-starry .btn-records:hover {
  background: #1a2745 !important;
  border-color: #64ffda !important;
  color: #64ffda !important;
}
.countdown-timer {
  margin-top: 20px;
  text-align: center;
}

.countdown-timer p {
  font-size: 16px;
  color: #ff4d4f; /* 使用红色提示用户 */
  font-weight: bold;
}

.main-card.disabled {
  opacity: 0.6; /* 半透明效果 */
  pointer-events: none; /* 禁止鼠标交互 */
}

/* 响应式设计 */

/* 平板适配 */
@media (max-width: 1024px) {
  .vip-recharge-page {
    padding: 15px;
  }
  
  .package-list {
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)) !important;
  }
}

/* 手机适配 */
@media (max-width: 768px) {
  .vip-recharge-page {
    padding: 10px;
  }

  .vip-recharge-page .main-card {
    padding: 15px;
    max-width: 100%;
  }

  .vip-recharge-page .page-title {
    font-size: 20px;
  }

  .current-vip-status {
    font-size: 13px;
  }

  .page-header {
    padding: 15px 10px;
  }

  .page-title {
    font-size: 1.3rem !important;
  }

  .package-list {
    flex-direction: column;
    gap: 10px;
  }

  .package-item {
    width: 100% !important;
    padding: 12px !important;
  }

  .package-name {
    font-size: 14px !important;
  }

  .package-price {
    font-size: 18px !important;
  }

  .package-description {
    font-size: 12px !important;
    min-height: auto;
  }

  .package-daily-limit {
    font-size: 11px;
  }

  .payment-method {
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 15px 10px;
  }

  .payment-option {
    flex-direction: row !important;
    justify-content: center;
    width: 100% !important;
    padding: 10px !important;
    margin: 0 !important;
  }

  .payment-logo {
    width: 30px !important;
  }

  .actions {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  .actions button {
    width: 100% !important;
    margin: 0 !important;
  }

  /* 模态框适配 */
  .ivu-modal {
    width: 95% !important;
    max-width: 100% !important;
  }

  .pay-modal-content {
    padding: 10px;
  }

  .pay-modal-content p {
    font-size: 14px;
  }

  .selected-package {
    margin: 15px 0;
    padding: 10px;
  }

  .countdown-timer p {
    font-size: 14px !important;
  }

  /* 触摸优化 */
  button,
  .package-item {
    min-height: 44px;
  }
}

/* 小屏手机适配 */
@media (max-width: 480px) {
  .page-title {
    font-size: 1.1rem !important;
  }
  
  .package-price {
    font-size: 16px !important;
  }
  
  .countdown-timer p {
    font-size: 14px !important;
  }
}

/**
 * 主题切换支持
 */

/* 星空主题（深色） */
.theme-starry .vip-recharge-page {
  background-color: #0a0e27;
}

.theme-starry .main-card {
  background-color: #1a1a2e;
  border-color: #2a2a4e;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
}

.theme-starry .page-title {
  background: linear-gradient(45deg, #64ffda, #00bcd4);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

.theme-starry .current-vip-status {
  color: #ccd6f6;
}

.theme-starry .package-item {
  background-color: #16213e;
  border-color: #2a2a4e;
}

.theme-starry .package-item:hover {
  border-color: #64ffda;
  box-shadow: 0 4px 12px rgba(100, 255, 218, 0.2);
}

.theme-starry .package-item.active {
  border-color: #64ffda;
  background-color: #1a2745;
}

.theme-starry .package-name {
  color: #ccd6f6;
}

.theme-starry .package-price {
  color: #64ffda;
}

.theme-starry .package-description {
  color: #8892b0;
}

.theme-starry .package-daily-limit {
  color: #64ffda;
}

.theme-starry .payment-option {
  background-color: #16213e;
  border-color: #2a2a4e;
}

.theme-starry .payment-option:hover {
  border-color: #64ffda;
  background-color: #1a2745;
}

.theme-starry .payment-option span {
  color: #ccd6f6;
}

.theme-starry .selected-package {
  background-color: #16213e;
}

/* 蓝色主题 */
.theme-blue .vip-recharge-page {
  background-color: #e3f2fd;
}

.theme-blue .main-card {
  background-color: #ffffff;
  border-color: #bbdefb;
}

.theme-blue .page-title {
  color: #1976d2;
}

.theme-blue .package-item:hover {
  border-color: #1976d2;
  box-shadow: 0 4px 12px rgba(25, 118, 210, 0.2);
}

.theme-blue .package-item.active {
  border-color: #1976d2;
  background-color: #e3f2fd;
}

.theme-blue .package-price {
  color: #1976d2;
}

.theme-blue .package-daily-limit {
  color: #1976d2;
}

/* 紫色主题 */
.theme-purple .vip-recharge-page {
  background-color: #f3e5f5;
}

.theme-purple .main-card {
  background-color: #ffffff;
  border-color: #e1bee7;
}

.theme-purple .page-title {
  color: #7b1fa2;
}

.theme-purple .package-item:hover {
  border-color: #7b1fa2;
  box-shadow: 0 4px 12px rgba(123, 31, 162, 0.2);
}

.theme-purple .package-item.active {
  border-color: #7b1fa2;
  background-color: #f3e5f5;
}

.theme-purple .package-price {
  color: #7b1fa2;
}

.theme-purple .package-daily-limit {
  color: #7b1fa2;
}

/* 绿色主题 */
.theme-green .vip-recharge-page {
  background-color: #e8f5e9;
}

.theme-green .main-card {
  background-color: #ffffff;
  border-color: #c8e6c9;
}

.theme-green .page-title {
  color: #388e3c;
}

.theme-green .package-item:hover {
  border-color: #388e3c;
  box-shadow: 0 4px 12px rgba(56, 142, 60, 0.2);
}

.theme-green .package-item.active {
  border-color: #388e3c;
  background-color: #e8f5e9;
}

.theme-green .package-price {
  color: #388e3c;
}

.theme-green .package-daily-limit {
  color: #388e3c;
}

/* 红色主题 */
.theme-red .vip-recharge-page {
  background-color: #ffebee;
}

.theme-red .main-card {
  background-color: #ffffff;
  border-color: #ffcdd2;
}

.theme-red .page-title {
  color: #d32f2f;
}

.theme-red .package-item:hover {
  border-color: #d32f2f;
  box-shadow: 0 4px 12px rgba(211, 47, 47, 0.2);
}

.theme-red .package-item.active {
  border-color: #d32f2f;
  background-color: #ffebee;
}

.theme-red .package-price {
  color: #d32f2f;
}

.theme-red .package-daily-limit {
  color: #d32f2f;
}

/* 橙色主题 */
.theme-orange .vip-recharge-page {
  background-color: #fff3e0;
}

.theme-orange .main-card {
  background-color: #ffffff;
  border-color: #ffe0b2;
}

.theme-orange .page-title {
  color: #f57c00;
}

.theme-orange .package-item:hover {
  border-color: #f57c00;
  box-shadow: 0 4px 12px rgba(245, 124, 0, 0.2);
}

.theme-orange .package-item.active {
  border-color: #f57c00;
  background-color: #fff3e0;
}

.theme-orange .package-price {
  color: #f57c00;
}

.theme-orange .package-daily-limit {
  color: #f57c00;
}

/* 经典主题 */
.theme-classic .vip-recharge-page {
  background-color: #f5f7fa;
}

.theme-classic .main-card {
  background-color: #ffffff;
  border-color: #e8eaec;
}

.theme-classic .page-title {
  color: #333;
}
</style>